/**
 * Sentry adapter. Both metrics are REAL: each `*From` builder reads what the API pulled from Sentry, and
 * returns null when Sentry has nothing to say about that product.
 */
import i18n from '@app/i18n';
import { ProductCrashFree, ProductResponseTime } from '@data/utils/types';
import { CrashFreeMetric, ResponseTimeMetric, TrendPoint } from './types';
import { crashFreeStatus, responseTimeStatus } from './thresholds';
import { shortDay } from './util';

// Percentages share one format (two decimals) so the cards read consistently.
const pct = (n: number): string => `${n.toFixed(2)}%`;
const formatMs = (ms: number): string => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
// Large counts read best with the locale's grouping separators.
const formatCount = (n: number): string => n.toLocaleString('en-IE');
const VERDICT_KEYS: Record<string, string> = { good: 'verdictFast', watch: 'verdictModerate', risk: 'verdictSlow' };

export const sentry = {
  /**
   * Real Sentry data. Returns null when Sentry has no usable release health for this product: either no
   * sessions, or too few for a percentage to mean anything (a rate over a handful of sessions moves
   * several points on one crash). The card then shows an unknown state instead of a reassuring 99.9%.
   */
  getCrashFreeSessionsFrom(crashFree: ProductCrashFree): CrashFreeMetric | null {
    if (crashFree.crashFreeRatePct === null || crashFree.sessionCount === 0) return null;

    const ratePct = crashFree.crashFreeRatePct;
    const crashFreeCount = Math.round((crashFree.sessionCount * ratePct) / 100);
    return {
      status: crashFreeStatus(ratePct),
      value: pct(ratePct),
      detail: i18n.t('productDashboard.sessionsDetail', {
        crashFree: formatCount(crashFreeCount),
        total: formatCount(crashFree.sessionCount),
      }),
      // A rolling window has no handoff anchor to compare against, so there is no before/after.
      // A day with too few sessions is a gap in the line, kept in place so the days stay evenly spaced.
      series: (crashFree.series ?? []).map(point =>
        point.ratePct === null
          ? { label: shortDay(point.date), value: 0, display: '', status: 'good' as const, gap: true }
          : {
              label: shortDay(point.date),
              value: point.ratePct,
              display: pct(point.ratePct),
              status: crashFreeStatus(point.ratePct),
            },
      ),
    };
  },

  /**
   * Real Sentry data. Returns null when Sentry has nothing for this product, which is the important
   * case: a client reads "0 ms" as perfect speed, so the card must say it doesn't know instead.
   *
   * The series is Sentry's daily p75 over its retention window, not snapshot history, so it carries no
   * handoff anchor — hence no `trend`. Days with no traffic are dropped rather than plotted as zero.
   */
  getResponseTimeFrom(responseTime: ProductResponseTime): ResponseTimeMetric | null {
    if (responseTime.p75Ms === null || responseTime.transactionCount === 0) return null;

    const ms = responseTime.p75Ms;
    const status = responseTimeStatus(ms);
    const series: TrendPoint[] = responseTime.series
      .filter(point => point.p75Ms !== null)
      .map(point => ({
        label: shortDay(point.date),
        value: point.p75Ms!,
        display: formatMs(point.p75Ms!),
        status: responseTimeStatus(point.p75Ms!),
      }));

    return {
      status,
      value: formatMs(ms),
      verdict: i18n.t(`productDashboard.${VERDICT_KEYS[status]}`),
      ms,
      series,
    };
  },
};
